import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleSchema from '@/components/feature/ArticleSchema';
import { getArticleBySlug, allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = article.metaTitle;
    }
  }, [article]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema article={article} />
      <Navbar />

      <article>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center flex-shrink-0"></i>
              <span className="text-foreground-800 line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                <i className="ri-book-open-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {article.tag}
              </span>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-3">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                Updated: June 2026
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {article.readTime}
              </span>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-6 md:space-y-8">
              {article.sections.map((section, idx) => (
                <div key={idx}>
                  {section.headingLevel === 'h2' ? (
                    <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                      {section.heading}
                    </h2>
                  ) : (
                    <h3 className="text-base md:text-lg font-semibold font-heading text-foreground-800 mt-5 mb-2">
                      {section.heading}
                    </h3>
                  )}
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="mb-3">
                      {p}
                    </p>
                  ))}
                  {section.listItems && section.listItems.length > 0 && (
                    <ul className="list-disc pl-5 space-y-2 mb-3">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              <div className="mt-10 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70 text-center">
                <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-3">
                  Ready to Explore the VigorBuy Spreadsheet?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-[500px] mx-auto">
                  Browse 14 curated categories, access QC tutorials, and discover trusted resources to make smarter
                  purchasing decisions today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    Back to Homepage
                    <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold whitespace-nowrap rounded-md border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    VigorBuy Sheet
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>

              {allArticles.length > 1 && (
                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">Related Articles</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {allArticles
                      .filter((a) => a.slug !== article.slug)
                      .slice(0, 4)
                      .map((related) => (
                        <Link
                          key={related.slug}
                          to={`/${related.slug}/`}
                          className="flex items-start gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-background-100 transition-all cursor-pointer"
                        >
                          <span className="inline-flex px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium flex-shrink-0 mt-0.5">
                            {related.tag}
                          </span>
                          <span className="text-xs md:text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                            {related.title}
                          </span>
                        </Link>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}